import React from "react";
import { Grid, MenuItem, Paper, Select, Typography } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { isEmpty, isNumber, sum } from "lodash";

import PageDomain from "../domain";
import PageConfig from "../config";
import SummeryWidget from "./summery-widget";
import { setAppSalesForecastData } from "../../../../actions/generic-data";

const MonthlyTargetPerformanceChart = ({ orderedSalesMap }) => {
  const canvasRef = React.useRef(null);
  const chartRef = React.useRef(null);

  const [chartType, setChartType] = React.useState("line");
  const [incrementPercentage, setIncrementPercentage] = React.useState(5);
  const [chartData, setChartData] = React.useState({});

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (isEmpty(orderedSalesMap)) return;
    const chartData = PageDomain.generateMonthlyTargetPerformanceChartDateset({
      orderedSalesMap,
      chartType,
      salesIncrement: incrementPercentage,
    });
    setChartData(chartData);
    dispatch(setAppSalesForecastData(chartData.accumulators));
  }, [orderedSalesMap, chartType, incrementPercentage, dispatch]);

  React.useEffect(() => {
    if (canvasRef.current == null || !window.Chart || isEmpty(chartData)) {
      return;
    }

    if (chartRef.current === null) {
      const ctx = canvasRef.current.getContext("2d");
      chartRef.current = new window.Chart(ctx, {
        type: chartType,
        data: {
          labels: chartData.labels ?? [],
          datasets: chartData.datasets ?? {},
        },
        options: {
          responsive: true,
        },
      });
    } else {
      chartRef.current.config.type = chartType;
      chartRef.current.data.datasets = chartData.datasets ?? [];
      chartRef.current.data.labels = chartData.labels ?? [];
      switch (chartType) {
        case "pie":
          chartRef.current.options.scales.x.display = false;
          chartRef.current.options.scales.y.display = false;
          chartRef.current.options.plugins.tooltip.callbacks.label = (
            context
          ) => {
            const total = sum(context.dataset.data);
            const currentValue = context.dataset.data[context.dataIndex];
            const percentage = ((currentValue / total) * 100).toFixed(2);
            return `${currentValue} (${percentage}%)`;
          };
          chartRef.current.options.responsive = false;
          break;
        case "line":
        default:
          chartRef.current.options.scales.x.display = true;
          chartRef.current.options.scales.y.display = true;
          break;
      }
      chartRef.current.update();
    }
  }, [chartData, chartType]);

  const currentSalesMonth = PageDomain.getCurrentSalesMonthEndDate({
    format: PageConfig.FULL_MONTH_NAME_FORMAT,
  });

  return (
    <Paper
      id="target-performance-chart"
      elevation={2}
      className="chart-paper monthly-target-performance-chart"
    >
      <Grid container direction="column" className="chart-panel">
        <Grid container direction="row">
          <Grid item xs={12} md={6} container direction="column">
            <Typography variant="h6">Monthly Target Performance</Typography>
            <Typography variant="caption" className="text-capitalize">
              Month: {currentSalesMonth}
            </Typography>
          </Grid>
          <Grid
            item
            md
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Grid
              item
              container
              direction="row"
              alignItems="center"
              className="flex-1 gap-x-8"
            >
              <Typography variant="caption">Chart Type</Typography>
              <Select
                variant="standard"
                value={chartType}
                onChange={(event) => {
                  setChartType(event.target.value);
                }}
              >
                <MenuItem value="line">Line</MenuItem>
                <MenuItem value="pie">Pie</MenuItem>
              </Select>
            </Grid>
            {chartType === "line" && (
              <Grid
                item
                container
                direction="row"
                alignItems="center"
                className="flex-1 gap-x-8"
              >
                <Typography variant="caption">Increment (%)</Typography>
                <Select
                  variant="standard"
                  value={
                    isNumber(incrementPercentage)
                      ? incrementPercentage.toString()
                      : incrementPercentage
                  }
                  onChange={(event) => {
                    setIncrementPercentage(parseInt(event.target.value));
                  }}
                >
                  <MenuItem value="5">5%</MenuItem>
                  <MenuItem value="10">10%</MenuItem>
                  <MenuItem value="20">20%</MenuItem>
                </Select>
              </Grid>
            )}
          </Grid>
          {!isEmpty(chartData.accumulators) && (
            <SummeryWidget accumulators={chartData.accumulators} />
          )}
        </Grid>
        <canvas ref={canvasRef}></canvas>
      </Grid>
    </Paper>
  );
};

export default MonthlyTargetPerformanceChart;
